import { useState, useEffect } from "react";
import { PaymentService, PayeeAccountVM, PayeeAccountTypesEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import { useDebounce } from "use-debounce";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../newComponents/form-fields/InputField";
import SelectField from "../../../newComponents/form-fields/SelectField";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import PayeeAccountsTopHeader from "./PayeeAccountsTopHeader";
import PayeeAccountCardItem from "./PayeeAccountCardItem";
import { GetImages } from "../../../utils/GetImages";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";

interface PayeeAccountsProps {
  className?: string;
}

const PayeeAccountsPage = ({ className = "" }: PayeeAccountsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [selectedPayeeAccount, setSelectedPayeeAccount] = useState<PayeeAccountVM | undefined>(undefined);
  const [payeeTypeOptions, setPayeeTypeOptions] = useState<{ Id: number; Name: string }[]>([]);
  const [isLoadingPayeeTypes, setIsLoadingPayeeTypes] = useState(false);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const check = usePermissions();
  const canWrite = check.canWrite("PayeeAccounts");

  // For Empty State
  const [hasAnyPayeeAccounts, setHasAnyPayeeAccounts] = useState<boolean>(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const handleFilterChange = (filter: string) => {
    updateFullyQualifiedFilter(filter, searchTerm);
  };

  const updateFullyQualifiedFilter = (filter: string, search: string) => {
    let baseFilter = "";

    switch (filter) {
      case "Organization":
        baseFilter = `PayeeAccountTypeId == ${PayeeAccountTypesEnum.Organization}`;
        break;
      case "All":
      default:
        baseFilter = "";
        break;
    }

    const searchFilter = search
      ? `AccountName.ToLower().Contains("${search.toLowerCase()}")`
      : "";

    const combinedFilter = [baseFilter, searchFilter].filter(Boolean).join(" &&");
    setFullyQualifiedFilter(combinedFilter || null);
  };

 

const {
  rows,
  loading,
  hasMore,
  rowCount,
  fetchData,
  currentPage,
  aggregateValues,
} = infiniteScrollHook({
  httpFetchDataFn: PaymentService.getApiPayeeAccountOrganizationlist, 
  FullyQualifiedFilters: fullyQualifiedFilter,
  pageSize: 10,
});

  useEffect(() => {
    updateFullyQualifiedFilter(activeFilter, debouncedSearchTerm);
  }, [debouncedSearchTerm, activeFilter]);

  useEffect(() => {
    if (rowCount > 0) {
      setHasAnyPayeeAccounts(true);
    }
  }, [rowCount]);

  useEffect(() => {
    setIsLoadingPayeeTypes(true);
    PaymentService.getApiPayeeAccountPayeeTypes()
      .then((response) => {
        const options = response?.success?.map((item: any) => ({
          Id: item.id,
          Name: item.name || "Unknown",
        })) || [];
        setPayeeTypeOptions(options);
        setIsLoadingPayeeTypes(false);
      })
      .catch((error) => {
        console.error("Error fetching payee types:", error);
        setIsLoadingPayeeTypes(false);
      });
  }, []);

  const methods = useForm<PayeeAccountVM>({
    defaultValues: {
      id: undefined,
      accountName: "",
      payeeAccountTypeId: undefined, // ✅ FIX 1: undefined rakho taake required validation kaam kare
      organizationId: organizationId,
    },
    mode: "onBlur",
  });

  const { handleSubmit, reset, formState: { isDirty } } = methods;

  const onSubmit = async (data: PayeeAccountVM) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        id: data.id,
        accountName: data.accountName,
        organizationId: String(organizationId),
        payeeAccountTypeId: Number(data.payeeAccountTypeId),
      };

      const apiCall = data.id
        ? PaymentService.putApiPayeeAccount
        : PaymentService.postApiPayeeAccount;

      await apiHelper(
        apiCall,
        {
          title: data.id
            ? "Payee Account Updated"
            : "Payee Account Added",
          successMessage: data.id
            ? "Payee account updated successfully"
            : "Payee account added successfully",
          failureMessage: data.id
            ? "Unable to update payee account"
            : "Unable to add payee account",
          showNotification: true,
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />
        },
        payload
      );

      setAddModalOpen(false);
      reset();
      fetchData(1);
    } catch (error) {
      console.error("Error submitting payee account:", error);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isAddModalOpen) {
      if (selectedPayeeAccount) {
        // Edit Mode - existing data se populate karo
        methods.reset(selectedPayeeAccount);
      } else {
        // Add Mode - payeeAccountTypeId undefined rakho
        methods.reset({
          id: undefined,
          accountName: "",
          payeeAccountTypeId: undefined, // ✅ FIX 2: Add mode mein undefined
          organizationId: organizationId,
        });
      }
    }
  }, [isAddModalOpen, selectedPayeeAccount, methods, organizationId]);

  const handleEditPayeeAccount = (payeeAccount: PayeeAccountVM) => {
    setSelectedPayeeAccount(payeeAccount);
    methods.reset(payeeAccount);
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedPayeeAccount(undefined);
    reset({
      id: undefined,
      accountName: "",
      payeeAccountTypeId: undefined, // ✅ FIX 3: Close par bhi undefined
      isOrganizationAccount: true,
      organizationId: organizationId,
      entityId: organizationId,
    });
  };

  const formId = selectedPayeeAccount ? "edit-payee-account-form" : "add-payee-account-form";

  return (
    <PermissionGuard permission="PayeeAccounts" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`payee-accounts-page content-wrapper container-fluid ${className}`}>
        {hasAnyPayeeAccounts && (
          <PayeeAccountsTopHeader
            PageHeading="Payee Accounts"
            Count={rowCount}
            ButtonName={canWrite ? "Add Payee Account" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleFilterChange={handleFilterChange}
            aggregateValuesFilters={aggregateValues}
            onClick={() => {
              setSelectedPayeeAccount(undefined);
              reset();
              setAddModalOpen(true);
            }}
          />
        )}

        <PayeeAccountCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
          }}
          onEditPayeeAccount={canWrite ? handleEditPayeeAccount : undefined}
          className="organisation-listing-card"
          hasAnyPayeeAccounts={hasAnyPayeeAccounts}
        />

        <RightSideModal
          isOpen={isAddModalOpen}
          onClose={handleModalClose}
          title={selectedPayeeAccount ? "Edit Payee Account" : "Add Payee Account"}
          buttonlabel={selectedPayeeAccount ? "Update Payee Account" : "Add Payee Account"}
          formId={formId}
          maxWidth={550}
          isDirty={isDirty}
          isSubmitDisabled={isSubmitting}
        >
          <Card className="no-bg">
            <FormProvider {...methods}>
              <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {
                console.log("Form validation errors:", error);
              })}>
                <div className="row">
                  <div className="col-12">
                    <InputField
                      name="accountName"
                      label="Payee Name"
                      type="text"
                      required
                      requiredMessage="Payee name is required"
                      placeholder="Enter Payee Name"
                      allowSpecialChars={false}
                      onlyAlphabets={true}
                      validation={{
                        required: "Payee name is required",
                        minLength: {
                          value: 3,
                          message: "Payee name must be at least 3 characters",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-12">
                    <SelectField
                      placeholderText="Select Account Type"
                      name="payeeAccountTypeId"
                      label="Account Type"
                      required
                      requiredMessage="Account Type is required"
                      options={payeeTypeOptions}
                      disabled={isLoadingPayeeTypes}
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
          </Card>
        </RightSideModal>
      </div>
    </PermissionGuard>
  );
};

export default PayeeAccountsPage;